/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { css } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import {
    Chip,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material'
import { useGetInvoicingRunsQuery } from '../invoicing.service'
import { InvoicingRun } from '../types/invoicing-run.type'
import { InvoicingRunReportDialog } from './invoicing-run-report-dialog'

const styles = {
    row: css`
        cursor: pointer;
    `,
}

const monthName = (m: number) =>
    ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]

const formatRange = (run: InvoicingRun) => {
    const from = `${monthName(run.fromMonth)} ${run.fromYear}`
    const to = `${monthName(run.toMonth)} ${run.toYear}`
    return from === to ? from : `${from} → ${to}`
}

export const InvoicingHistoryView: React.FC = () => {
    const { t } = useTranslation()
    const [page, setPage] = useState(0)
    const [itemsPerPage, setItemsPerPage] = useState(20)
    const { data, isLoading } = useGetInvoicingRunsQuery({ page, itemsPerPage })
    const [selected, setSelected] = useState<InvoicingRun | null>(null)

    if (isLoading) return <LinearProgress />
    const runs = data?.data ?? []
    const total = data?.total ?? 0

    return (
        <Paper elevation={0}>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('TEXT_RUN_AT', 'Run at')}</TableCell>
                            <TableCell>{t('TEXT_RUN_BY', 'Run by')}</TableCell>
                            <TableCell>{t('TEXT_RANGE', 'Range')}</TableCell>
                            <TableCell align="right">{t('TEXT_COMPANIES_PROCESSED', 'Companies')}</TableCell>
                            <TableCell align="right">{t('TEXT_COMPANIES_INVOICED', 'Invoiced')}</TableCell>
                            <TableCell align="right">{t('TEXT_COMPANIES_ORPHANED', 'Orphaned')}</TableCell>
                            <TableCell align="right">{t('TEXT_COMPANIES_FAILED', 'Failed')}</TableCell>
                            <TableCell>{t('TEXT_STATUS', 'Status')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {runs.map((run) => (
                            <TableRow key={run.id} hover css={styles.row} onClick={() => setSelected(run)}>
                                <TableCell>{new Date(run.runAt).toLocaleString()}</TableCell>
                                <TableCell>{run.runByUserName}</TableCell>
                                <TableCell>{formatRange(run)}</TableCell>
                                <TableCell align="right">{run.totalCompanies}</TableCell>
                                <TableCell align="right">{run.companiesInvoiced}</TableCell>
                                <TableCell
                                    align="right"
                                    style={{ color: run.companiesOrphaned > 0 ? '#d32f2f' : undefined }}
                                >
                                    {run.companiesOrphaned}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    style={{ color: run.companiesFailed > 0 ? '#d32f2f' : undefined }}
                                >
                                    {run.companiesFailed}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={run.status}
                                        color={run.status === 'Completed' ? 'success' : 'warning'}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                        {runs.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    {t('TEXT_NO_RUNS_YET', 'No invoicing runs recorded yet.')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={itemsPerPage}
                onRowsPerPageChange={(e) => {
                    setItemsPerPage(Number(e.target.value))
                    setPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50]}
            />
            <InvoicingRunReportDialog run={selected} onClose={() => setSelected(null)} />
        </Paper>
    )
}
